import { useSEO } from "../../hooks/useSEO";
import { seoConfig } from "../../utils/seoConfig";
import { WanderfulHero } from './WanderfulHero';
import { Navbar, Footer } from './LandingPage';

const SchoolsPage = () => {
  useSEO(seoConfig.schools);

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <WanderfulHero
        title="Your nights are free again."
        subtitle="Grade tests while you focus on actually teaching. Works with CBSE, ICSE, State Boards—however you mark papers."
        cta1Text="Get Started"
        cta2Text=""
      />
      
      {/* Specific Sections based on user request */}
      <section className="py-8 sm:py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-[#f9f9f9] rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-16">
            <div className="text-center mb-12">
              <span className="text-sm font-bold text-slate-900 uppercase tracking-widest bg-gray-100 px-4 py-1.5 rounded-full">Coverage</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-black mt-4 mb-4">All Your Exams. One System.</h2>
              <p className="text-gray-600 max-w-2xl mx-auto font-medium">Class 8 unit test? Board exam? Practice test? We handle them all the same way.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 shadow-sm hover:border-black hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
                <div className="w-8 h-8 rounded-full bg-gray-100 text-slate-900 flex items-center justify-center font-bold text-sm mb-4">01</div>
                <h3 className="font-bold text-lg mb-2 text-slate-900">Unit Tests</h3>
                <p className="text-gray-600 text-sm">Marks ready for your class assembly. No delays.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:border-black hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
                <div className="w-8 h-8 rounded-full bg-gray-100 text-slate-900 flex items-center justify-center font-bold text-sm mb-4">02</div>
                <h3 className="font-bold text-lg mb-2 text-slate-900">Term Exams</h3>
                <p className="text-gray-600 text-sm">600 papers? 2000 papers? All graded to your standards.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:border-black hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
                <div className="w-8 h-8 rounded-full bg-gray-100 text-slate-900 flex items-center justify-center font-bold text-sm mb-4">03</div>
                <h3 className="font-bold text-lg mb-2 text-slate-900">Board Practice</h3>
                <p className="text-gray-600 text-sm">Strict marking. Tough feedback. Get them exam-ready.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Emphasis as Bento Grid (Dark Mode) */}
      <section className="py-8 sm:py-12 md:py-24 bg-black text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <span className="text-sm font-bold text-white/50 uppercase tracking-widest">What Actually Helps</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-4">More Time. Better Feedback.</h2>
            <p className="text-gray-400 max-w-2xl mx-auto font-medium">Spend your energy on what matters—teaching and mentoring. Not marking papers.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
            {/* Card 1: Large */}
            <div className="md:col-span-2 p-6 sm:p-8 lg:p-10 bg-neutral-900 rounded-2xl sm:rounded-3xl border border-neutral-800 hover:border-neutral-700 transition-all duration-300 flex flex-col justify-between group cursor-pointer">
              <div>
                <h3 className="font-bold text-2xl mb-3 text-white">You Get Your Evenings Back</h3>
                <p className="text-neutral-400 text-sm leading-relaxed max-w-md">Stop marking papers till midnight. Your family gets you. Your sanity returns.</p>
              </div>
              <span className="text-8xl font-black text-neutral-800 group-hover:text-neutral-700 transition-colors">01</span>
            </div>

            {/* Card 2: Tall */}
            <div className="p-6 sm:p-8 lg:p-10 bg-neutral-900 rounded-2xl sm:rounded-3xl border border-neutral-800 hover:border-neutral-700 transition-all duration-300 flex flex-col justify-between group cursor-pointer">
              <div>
                <h3 className="font-bold text-2xl mb-3 text-white">Parents Get It. Really Get It.</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">Share reports that show exactly where their child struggled. No arguments. Just facts.</p>
              </div>
              <span className="text-8xl font-black text-neutral-800 group-hover:text-neutral-700 transition-colors">02</span>
            </div>

            {/* Card 3: Wide */}
            <div className="md:col-span-3 p-10 bg-neutral-900 rounded-3xl border border-neutral-800 hover:border-neutral-700 transition-all duration-300 flex flex-col md:flex-row md:items-center justify-between group cursor-pointer">
              <div className="max-w-xl">
                <h3 className="font-bold text-2xl mb-3 text-white">Works with NEP 2020. Just Works.</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">We evaluate the way NEP 2020 wants you to teach. Competencies, not just rote. You set the rubric.</p>
              </div>
              <span className="text-8xl font-black text-neutral-800 group-hover:text-neutral-700 transition-colors">03</span>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Emphasis */}
      <section className="py-8 sm:py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="bg-slate-900 text-white rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-16 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">Your Students' Data Stays Safe</h2>
            <p className="text-white/70 mb-8 max-w-2xl mx-auto">DPDP compliant. We don't sell data. Your papers stay yours. Period.</p>
            <div className="flex justify-center gap-4 text-xs font-bold uppercase tracking-wider">
              <span className="bg-white/10 px-4 py-2 rounded-full">DPDP Compliant</span>
              <span className="bg-white/10 px-4 py-2 rounded-full">ISO 27001</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SchoolsPage;
